import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Calendar, Check, ChevronDown, ChevronUp, Copy, Folder, Image as ImageIcon, Loader2, MoreHorizontal,
  Plus, Trash2, X, Zap,
} from 'lucide-react';
import { useAuthDrawer } from '../../context/AuthDrawerContext';
import { apiErrorText, deleteTransfer, expiryLabel, formatBytes, getConfig, sendTransfer, embedParentOrigin } from './api';
import { entriesFromDrop, entriesFromInput } from './readDrop';

// Two skins for one component: the public pages sit on the site's dark theme or
// the landing's light/dark switch; the embed sits on a client's page (light).
const SKINS = {
  dark: {
    card: 'bg-[#302f2c] border border-[#4a3520] text-white',
    muted: 'text-[#9a968d]',
    tile: 'bg-white/10 hover:bg-white/15',
    line: 'border-white/15',
    input: 'text-white placeholder-white/40',
    pop: 'bg-[#1a1c1b] border border-white/15 shadow-2xl',
    hover: 'hover:bg-white/10',
    ghost: 'border border-white/20 hover:bg-white/10',
  },
  light: {
    card: 'bg-white border border-gray-200 text-gray-900 shadow-2xl',
    muted: 'text-gray-500',
    tile: 'bg-blue-50 hover:bg-blue-100',
    line: 'border-gray-200',
    input: 'text-gray-900 placeholder-gray-400',
    pop: 'bg-white border border-gray-200 shadow-2xl',
    hover: 'hover:bg-gray-100',
    ghost: 'border border-gray-300 hover:bg-gray-100',
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UPGRADE_HINT = 'Included in paid plans. Contact us to upgrade.';

const TransferSend = ({ skin = 'dark', onConfig, embedded = false }) => {
  // In an embed the card sits on someone else's page, so use a lighter shadow that fits inside the frame.
  const s = embedded ? { ...SKINS[skin], card: SKINS[skin].card.replace('shadow-2xl', 'shadow-lg') } : SKINS[skin];
  const { openAuth } = useAuthDrawer();
  const fileRef = useRef(null);
  const folderRef = useRef(null);
  const bgRef = useRef(null);
  const expiryRef = useRef(null);

  const [config, setConfig] = useState(null);
  const [error, setError] = useState('');
  const [entries, setEntries] = useState([]); // [{ file, path }]
  const [expiry, setExpiry] = useState(null);
  const [expiryOpen, setExpiryOpen] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);
  const [mode, setMode] = useState('email'); // 'email' | 'link'
  const [recipientsText, setRecipientsText] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [password, setPassword] = useState('');
  const [maxDownloads, setMaxDownloads] = useState('');
  const [notify, setNotify] = useState(false);
  const [bgFile, setBgFile] = useState(null);
  const [bgPreview, setBgPreview] = useState('');
  const [choosing, setChoosing] = useState(false); // anonymous sender: log in / sign up / guest
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(0);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    getConfig()
      .then((c) => {
        setConfig(c);
        setExpiry((cur) => cur ?? c.entitlements.default_expiry_days);
        if (onConfig) onConfig(c);
      })
      .catch(() => setError('File transfer is not available right now.'));
  }, [onConfig]);

  // Folder picking needs a non-standard attribute React does not forward as a prop.
  useEffect(() => {
    folderRef.current?.setAttribute('webkitdirectory', '');
    folderRef.current?.setAttribute('directory', '');
  }, [config]);

  useEffect(() => {
    if (!expiryOpen) return undefined;
    const close = (e) => { if (!expiryRef.current?.contains(e.target)) setExpiryOpen(false); };
    const esc = (e) => e.key === 'Escape' && setExpiryOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [expiryOpen]);

  useEffect(() => {
    if (!bgFile) { setBgPreview(''); return undefined; }
    const url = URL.createObjectURL(bgFile);
    setBgPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [bgFile]);

  const addEntries = useCallback((list) => {
    setEntries((prev) => [...prev, ...list]);
    setError('');
  }, []);

  const onDrop = async (e) => {
    e.preventDefault();
    addEntries(await entriesFromDrop(e.dataTransfer));
  };

  if (!config) {
    return <div className={`${s.card} rounded-2xl p-8 text-center ${s.muted}`}>{error || 'Loading…'}</div>;
  }

  const ent = config.entitlements;
  const accent = config.brand.brand_accent || '#FF6B00';
  const paid = ent.plan === 'paid';
  const total = entries.reduce((n, e) => n + e.file.size, 0);
  const recipients = recipientsText.split(/[,\s;]+/).filter(Boolean);
  const tooMany = recipients.length > ent.max_recipients;
  const notifyEmail = config.logged_in ? 'your account email' : senderEmail.trim();

  const afterAuth = () => { getConfig().then(setConfig).catch(() => {}); };

  if (config.who_can_send === 'login' && !config.logged_in) {
    return (
      <div className={`${s.card} rounded-2xl p-8 text-center`}>
        <p className="mb-4">Sign in to send files.</p>
        <div className="flex flex-wrap justify-center gap-3">
          <button type="button" onClick={() => openAuth('login', { onSuccess: afterAuth })}
            className="px-6 min-h-[48px] rounded-full text-white font-semibold" style={{ background: accent }} data-testid="transfer-login">
            Log in
          </button>
          <button type="button" onClick={() => openAuth('register', { onSuccess: afterAuth })}
            className={`px-6 min-h-[48px] rounded-full font-semibold ${s.ghost}`} data-testid="transfer-register">
            Create account
          </button>
        </div>
      </div>
    );
  }

  const validate = () => {
    if (!entries.length) return 'Add at least one file.';
    if (mode === 'email') {
      if (!recipients.length) return 'Add at least one email address, or switch to Create link.';
      if (tooMany) return `You can email up to ${ent.max_recipients} people per transfer on your plan.`;
      const bad = recipients.find((r) => !EMAIL_RE.test(r));
      if (bad) return `"${bad}" is not a valid email address.`;
    }
    if (senderEmail.trim() && !EMAIL_RE.test(senderEmail.trim())) return 'Your email address looks wrong.';
    if (notify && !config.logged_in && !senderEmail.trim()) return 'Enter your email to be told when your files are downloaded.';
    return '';
  };

  const doSend = async () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    setError('');
    setSending(true);
    setSent(0);
    try {
      const res = await sendTransfer({
        entries,
        background: bgFile,
        onProgress: setSent,
        options: {
          expiry_days: expiry,
          send_mode: mode,
          recipients: mode === 'email' ? recipients : [],
          title,
          message,
          password: password || null,
          sender_name: senderName,
          sender_email: senderEmail.trim() || null,
          notify_on_download: notify,
          max_downloads: maxDownloads ? Number(maxDownloads) : null,
          // Only a label for the admin list; the server checks the origin against the embed allow-list.
          via_embed: embedded,
          source_origin: embedded ? embedParentOrigin() : null,
        },
      });
      setResult(res);
    } catch (err) {
      setError(apiErrorText(err, 'Upload failed. Check your connection and try again.'));
    } finally {
      setSending(false);
    }
  };

  // Anonymous senders get a choice first. Logging in or signing up happens in the
  // drawer over this page, so the picked files are still here afterwards and the
  // upload carries on under the new account.
  const requestSend = () => {
    const problem = validate();
    if (problem) { setError(problem); return; }
    if (!config.logged_in && config.who_can_send === 'anyone') { setChoosing(true); return; }
    doSend();
  };

  const authThenSend = (authMode) => {
    openAuth(authMode, { onSuccess: () => { setChoosing(false); afterAuth(); doSend(); } });
  };

  const reset = () => {
    setEntries([]); setResult(null); setTitle(''); setMessage(''); setPassword('');
    setRecipientsText(''); setSent(0); setBgFile(null); setMaxDownloads(''); setNotify(false);
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(result.link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const removeNow = async () => {
    await deleteTransfer(result.transfer_id, result.manage_token);
    reset();
  };

  if (result) {
    return (
      <div className={`${s.card} rounded-2xl p-6 sm:p-8 text-center`} data-testid="transfer-success">
        <div className="inline-flex w-12 h-12 rounded-full items-center justify-center mb-4" style={{ background: `${accent}22`, color: accent }}>
          <Check className="w-6 h-6" />
        </div>
        <h2 className="text-xl font-bold mb-1">Your files are ready to share</h2>
        <p className={`${s.muted} text-sm mb-5`}>
          {result.expires_at
            ? `Available until ${new Date(result.expires_at).toLocaleDateString(undefined, { dateStyle: 'long' })}.`
            : 'These files never expire.'}
          {mode === 'email' ? ' We emailed the link to your recipients.' : ''}
        </p>
        <div className="flex gap-2 mb-4">
          <input readOnly value={result.link} onFocus={(e) => e.target.select()} aria-label="Share link"
            className={`flex-1 min-w-0 rounded-lg px-3 py-3 text-sm bg-transparent border ${s.line} ${s.input}`} data-testid="transfer-link" />
          <button type="button" onClick={copyLink} className="px-4 min-h-[44px] rounded-lg text-white font-semibold inline-flex items-center gap-2"
            style={{ background: accent }} data-testid="transfer-copy">
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}{copied ? 'Copied' : 'Copy'}
          </button>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          <button type="button" onClick={reset} className={`px-5 min-h-[44px] rounded-full font-medium ${s.ghost}`}>Send more</button>
          <button type="button" onClick={removeNow} className="px-5 min-h-[44px] rounded-full text-red-500 inline-flex items-center gap-2 font-medium">
            <Trash2 className="w-4 h-4" /> Delete now
          </button>
        </div>
      </div>
    );
  }

  const pct = total ? Math.min(100, Math.round((sent / total) * 100)) : 0;
  const underline = `w-full bg-transparent py-3 outline-none ${s.input}`;
  const dis = sending;

  const Locked = ({ label }) => (
    <div className="flex items-center justify-between py-3 opacity-60" title={UPGRADE_HINT} aria-disabled="true" data-testid="transfer-locked">
      <span className="text-sm">{label}</span>
      <Zap className="w-4 h-4 text-purple-500" aria-label="Paid plan" />
    </div>
  );

  return (
    <div className={`${s.card} rounded-2xl relative ${panelOpen ? 'lg:w-[44rem] lg:max-w-[calc(100vw-2rem)]' : ''}`}>
      <div className={panelOpen ? 'lg:grid lg:grid-cols-[22rem_minmax(0,1fr)]' : ''}>
        {/* ---- main column ---- */}
        <div className="p-4" onDragOver={(e) => e.preventDefault()} onDrop={onDrop} data-testid="transfer-dropzone">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => fileRef.current?.click()} disabled={dis}
              className={`${s.tile} rounded-xl min-h-[88px] flex flex-col items-center justify-center gap-2 text-sm font-medium transition-colors`} data-testid="transfer-add-files">
              <span className="w-8 h-8 rounded-full text-white inline-flex items-center justify-center" style={{ background: accent }}><Plus className="w-5 h-5" /></span>
              Add files
            </button>
            <button type="button" onClick={() => folderRef.current?.click()} disabled={dis}
              className={`${s.tile} rounded-xl min-h-[88px] flex flex-col items-center justify-center gap-2 text-sm font-medium transition-colors`} data-testid="transfer-add-folders">
              <span className="relative inline-flex"><Folder className="w-8 h-8" style={{ color: accent }} /><Plus className="w-3 h-3 absolute -right-1 -bottom-1 rounded-full text-white" style={{ background: accent }} /></span>
              Add folders
            </button>
          </div>
          <input ref={fileRef} type="file" multiple hidden onChange={(e) => { addEntries(entriesFromInput(e.target.files)); e.target.value = ''; }} data-testid="transfer-file-input" />
          <input ref={folderRef} type="file" multiple hidden onChange={(e) => { addEntries(entriesFromInput(e.target.files)); e.target.value = ''; }} data-testid="transfer-folder-input" />

          {entries.length > 0 && (
            <ul className="mt-3 space-y-1 max-h-40 overflow-y-auto" data-testid="transfer-file-list">
              {entries.map((e, i) => (
                <li key={`${e.path || e.file.name}-${i}`} className="flex items-center gap-2 text-sm py-1">
                  <span className="truncate flex-1" title={e.path || e.file.name}>{e.path || e.file.name}</span>
                  <span className={`${s.muted} text-xs`}>{formatBytes(e.file.size)}</span>
                  {!dis && (
                    <button type="button" aria-label={`Remove ${e.file.name}`} onClick={() => setEntries(entries.filter((_, j) => j !== i))}
                      className="w-8 h-8 inline-flex items-center justify-center"><X className="w-4 h-4" /></button>
                  )}
                </li>
              ))}
              <li className={`${s.muted} text-xs text-right`}>{entries.length} file{entries.length !== 1 ? 's' : ''} · {formatBytes(total)}</li>
            </ul>
          )}

          {!paid && (
            <p className={`${s.muted} text-xs text-center mt-3`}>
              Get unlimited transfers · <a href="/contact" target="_blank" rel="noopener noreferrer"
                className="font-semibold text-purple-600 inline-flex items-center gap-1"><Zap className="w-3 h-3" />Increase limit</a>
            </p>
          )}

          <div className={`mt-2 divide-y ${s.line} [&>*]:border-inherit text-sm`}>
            {mode === 'email' && (
              <label className="flex items-center gap-2">
                <input type="text" inputMode="email" value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} disabled={dis}
                  placeholder="Email to" aria-label="Email to" className={underline} data-testid="transfer-recipients" />
                <span className={`text-xs whitespace-nowrap ${tooMany ? 'text-red-500 font-semibold' : s.muted}`} data-testid="transfer-recipient-count">
                  {recipients.length} of {ent.max_recipients}
                </span>
              </label>
            )}
            {!config.logged_in && (
              <input type="email" value={senderEmail} onChange={(e) => setSenderEmail(e.target.value)} disabled={dis}
                placeholder="Your email" aria-label="Your email" className={underline} data-testid="transfer-sender-email" />
            )}
            <input type="text" maxLength={120} value={title} onChange={(e) => setTitle(e.target.value)} disabled={dis}
              placeholder="Title" aria-label="Title" className={underline} data-testid="transfer-title" />
            <textarea rows={2} maxLength={2000} value={message} onChange={(e) => setMessage(e.target.value)} disabled={dis}
              placeholder="Message" aria-label="Message" className={`${underline} resize-none`} data-testid="transfer-message" />
          </div>

          {/* ---- expiry + more ---- */}
          <div className="mt-3 flex gap-2">
            <div className="relative flex-1 min-w-0" ref={expiryRef}>
              <button type="button" aria-haspopup="listbox" aria-expanded={expiryOpen} onClick={() => setExpiryOpen((o) => !o)} disabled={dis}
                className={`w-full min-h-[48px] rounded-xl px-3 flex items-center gap-2 text-sm font-medium ${s.ghost}`} data-testid="transfer-expiry" style={{ color: accent }}>
                <Calendar className="w-4 h-4" />
                <span className="flex-1 text-left">{expiryLabel(expiry)}</span>
                {expiryOpen ? <ChevronUp className="w-4 h-4 text-current" /> : <ChevronDown className="w-4 h-4 text-current" />}
              </button>
              {expiryOpen && (
                <ul role="listbox" aria-label="Keep for" className={`${s.pop} absolute bottom-full left-0 mb-2 w-60 max-w-[80vw] rounded-xl py-2 z-30`} data-testid="transfer-expiry-list">
                  {ent.expiry_choices.map((c) => (
                    <li key={c.days} role="option" aria-selected={c.days === expiry} aria-disabled={c.locked}>
                      <button type="button" disabled={c.locked} title={c.locked ? UPGRADE_HINT : undefined}
                        onClick={() => { setExpiry(c.days); setExpiryOpen(false); }}
                        className={`w-full min-h-[44px] px-4 flex items-center gap-2 text-sm text-left ${c.locked ? 'opacity-50 cursor-not-allowed' : s.hover}`}
                        style={c.days === expiry ? { color: accent } : undefined} data-testid={`transfer-expiry-${c.days}`}>
                        <span>{expiryLabel(c.days)}</span>
                        {c.free && <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Free</span>}
                        <span className="ml-auto">
                          {c.days === expiry ? <Check className="w-4 h-4" /> : c.locked ? <Zap className="w-4 h-4 text-purple-500" /> : null}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <button type="button" aria-label="More options" aria-expanded={panelOpen} aria-controls="transfer-options" onClick={() => setPanelOpen((o) => !o)}
              className={`w-14 min-h-[48px] rounded-xl inline-flex items-center justify-center ${s.ghost} ${panelOpen ? 'ring-2 ring-blue-500' : ''}`} data-testid="transfer-more">
              <MoreHorizontal className="w-5 h-5" />
            </button>
          </div>

          {error && (
            <p role="alert" className="mt-3 text-sm text-red-500" data-testid="transfer-error">
              {error}
              {mode === 'email' && !recipients.length && entries.length > 0 && (
                <button type="button" onClick={() => { setMode('link'); setError(''); }} className="ml-2 underline font-semibold">Create a link instead</button>
              )}
            </p>
          )}

          {sending ? (
            <div className="mt-4" data-testid="transfer-progress">
              <div className="h-2 rounded-full bg-black/20 overflow-hidden">
                <div className="h-full transition-all" style={{ width: `${pct}%`, background: accent }} />
              </div>
              <p className={`${s.muted} text-xs mt-2 inline-flex items-center gap-2`}>
                <Loader2 className="w-3 h-3 animate-spin" /> Uploading {pct}% · {formatBytes(sent)} of {formatBytes(total)}
              </p>
            </div>
          ) : choosing ? (
            <div className="mt-4 text-center" data-testid="transfer-choice">
              <p className="text-sm font-semibold mb-1">Keep track of your transfers?</p>
              <p className={`${s.muted} text-xs mb-3`}>Log in or create a free account, or just send as a guest.</p>
              <div className="grid gap-2">
                <button type="button" onClick={() => authThenSend('login')} className="min-h-[48px] rounded-full text-white font-semibold"
                  style={{ background: accent }} data-testid="transfer-choice-login">Log in</button>
                <button type="button" onClick={() => authThenSend('register')} className={`min-h-[48px] rounded-full font-semibold ${s.ghost}`}
                  data-testid="transfer-choice-register">Create account</button>
                <button type="button" onClick={() => { setChoosing(false); doSend(); }} className="min-h-[44px] text-sm underline"
                  data-testid="transfer-choice-guest">Continue as guest</button>
              </div>
            </div>
          ) : (
            <button type="button" onClick={requestSend} disabled={!entries.length}
              className="mt-4 w-full min-h-[48px] rounded-xl text-white font-semibold disabled:opacity-40 transition-opacity"
              style={{ background: accent }} data-testid="transfer-send">
              Transfer
            </button>
          )}
        </div>

        {/* ---- options panel ---- */}
        {panelOpen && (
          <div id="transfer-options" className={`p-4 border-t lg:border-t-0 lg:border-l ${s.line} text-sm`} data-testid="transfer-options">
            <fieldset className="space-y-2 pb-3" disabled={dis}>
              <legend className="sr-only">How to send</legend>
              {[['email', 'Send email'], ['link', 'Create link']].map(([v, label]) => (
                <label key={v} className="flex items-center gap-3 min-h-[36px] cursor-pointer">
                  <input type="radio" name="transfer-mode" value={v} checked={mode === v} onChange={() => { setMode(v); setError(''); }}
                    className="w-5 h-5" style={{ accentColor: accent }} data-testid={`transfer-mode-${v}`} />
                  <span className="font-medium">{label}</span>
                </label>
              ))}
            </fieldset>

            <div className={`divide-y ${s.line} border-t ${s.line} [&>*]:border-inherit`}>
              {ent.features.password && (
                <label className="block py-3">
                  <span className={`${s.muted} text-xs`}>Password</span>
                  <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} disabled={dis}
                    placeholder="Set password" className={`${underline} !py-1`} data-testid="transfer-password" />
                </label>
              )}

              {ent.features.download_limit ? (
                <label className="block py-3">
                  <span className={`${s.muted} text-xs`}>Download limit</span>
                  <input type="number" min="1" max="100000" value={maxDownloads} onChange={(e) => setMaxDownloads(e.target.value)} disabled={dis}
                    placeholder="Unlimited downloads" className={`${underline} !py-1`} data-testid="transfer-max-downloads" />
                </label>
              ) : <Locked label="Download limit" />}

              <div className="py-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} disabled={dis} className="mt-1 w-4 h-4" data-testid="transfer-notify" />
                  <span>Email me when my files are downloaded
                    {notify && !notifyEmail && <span className="block text-xs text-amber-600">Enter your email in the main box first.</span>}
                    {notify && notifyEmail && <span className={`block text-xs ${s.muted}`}>We&apos;ll email {notifyEmail} once, on the first download.</span>}
                  </span>
                </label>
              </div>

              {!config.logged_in && (
                <label className="block py-3">
                  <span className={`${s.muted} text-xs`}>Your name (shown to recipients)</span>
                  <input type="text" maxLength={120} value={senderName} onChange={(e) => setSenderName(e.target.value)} disabled={dis}
                    placeholder="Optional" className={`${underline} !py-1`} data-testid="transfer-sender-name" />
                </label>
              )}

              {ent.features.custom_background ? (
                <div className="py-3">
                  <span className={`${s.muted} text-xs block mb-1`}>Appearance</span>
                  <input ref={bgRef} type="file" accept="image/png,image/jpeg,image/webp" hidden data-testid="transfer-bg-input"
                    onChange={(e) => {
                      const f = e.target.files[0];
                      e.target.value = '';
                      if (f && f.size > 5 * 1024 * 1024) { setError('Background image must be 5 MB or smaller.'); return; }
                      if (f) setBgFile(f);
                    }} />
                  {bgFile ? (
                    <div className="flex items-center gap-3">
                      <img src={bgPreview} alt="" className="w-16 h-10 object-cover rounded" />
                      <span className="flex-1 truncate">{bgFile.name}</span>
                      <button type="button" aria-label="Remove background" onClick={() => setBgFile(null)} className="w-10 h-10 inline-flex items-center justify-center"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => bgRef.current?.click()} disabled={dis} className="inline-flex items-center gap-2 min-h-[44px]" data-testid="transfer-bg-button">
                      <ImageIcon className="w-4 h-4" /> Customize background
                    </button>
                  )}
                </div>
              ) : <Locked label="Customize background" />}
            </div>

            {!paid && (
              <p className={`${s.muted} text-xs pt-3`}>
                Options marked <Zap className="inline w-3 h-3 text-purple-500" /> are part of a paid plan.{' '}
                <a href="/contact" target="_blank" rel="noopener noreferrer" className="font-semibold text-purple-600">Contact us to upgrade</a>.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransferSend;
